import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../utils/axios';
import type { MarketRecord } from '../types/index';

type UseMarketsParams = {
  enabled?: boolean;
  organizationId?: number | string | null;
  page?: number;
  pageSize?: number;
  scope?: 'all' | 'organization';
};

type UseMarketsResult = {
  data: MarketRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

type MarketsState = {
  data: MarketRecord[];
  error: unknown;
  isLoading: boolean;
  requestKey: string;
};

export async function getMarkets(
  organizationId: number | string,
  page = 0,
  pageSize = 50
): Promise<MarketRecord[]> {
  const response = await axios.get('/market', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAllMarkets(
  organizationId?: number | string
): Promise<MarketRecord[]> {
  const response = await axios.get('/market/all', {
    params: { organizationId },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export default function useMarkets({
  enabled = true,
  organizationId,
  page = 0,
  pageSize = 50,
  scope = 'all',
}: UseMarketsParams = {}): UseMarketsResult {
  const requestIdRef = useRef(0);
  const requestKey = useMemo(
    () => `${scope}:${String(organizationId || '')}:${page}:${pageSize}`,
    [organizationId, page, pageSize, scope],
  );
  const [state, setState] = useState<MarketsState>({
    data: [],
    error: null,
    isLoading: enabled,
    requestKey: '',
  });

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!enabled || !organizationId) {
      setState({ data: [], error: null, isLoading: false, requestKey });
      return;
    }

    setState({ data: [], error: null, isLoading: true, requestKey });

    try {
      const nextMarkets = scope === 'organization'
        ? await getMarkets(organizationId, page, pageSize)
        : await getAllMarkets(organizationId);
      if (requestId !== requestIdRef.current) return;
      setState({ data: nextMarkets, error: null, isLoading: false, requestKey });
    } catch (nextError) {
      if (requestId !== requestIdRef.current) return;
      setState({ data: [], error: nextError, isLoading: false, requestKey });
    }
  }, [enabled, organizationId, page, pageSize, requestKey, scope]);

  useEffect(() => {
    refetch().catch(() => null);
    return () => {
      requestIdRef.current += 1;
    };
  }, [refetch]);

  const isCurrentRequest = state.requestKey === requestKey;
  const shouldLoad = Boolean(enabled && organizationId);

  return {
    data: isCurrentRequest ? state.data : [],
    error: isCurrentRequest ? state.error : null,
    isLoading: shouldLoad && (!isCurrentRequest || state.isLoading),
    refetch,
  };
}
